import type { NextSeoProps } from "next-seo";
import { type FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { DefaultTitleTemplate } from "shared/components/meta/consts";
import { useDynamicMetaTags, useKeywordMetaTags } from "shared/components/meta/hooks";
import { useCanonicalUrl } from "shared/hooks";
import { getRssFeedLink } from "shared/lib/utils";

import { PageSeo } from "scmp-app/components/meta";
import type { authorSeoAuthor$key } from "scmp-app/queries/__generated__/authorSeoAuthor.graphql";

type Props = {
  extraSeoOptions?: NextSeoProps;
  reference: authorSeoAuthor$key;
};

export const AuthorSeo: FunctionComponent<Props> = ({
  extraSeoOptions = {},
  reference: reference_,
}) => {
  const author = useFragment(
    graphql`
      fragment authorSeoAuthor on Author {
        entityId
        bio
        name
        urlAlias
        ...hooksUseDynamicMetaTags
      }
    `,
    reference_,
  );

  const rssFeedLink = getRssFeedLink(author.entityId);

  const metaTags = useDynamicMetaTags(author);
  const keywordTags = useKeywordMetaTags(metaTags.controlled.keywords ?? author.name);
  const { canonicalUrl } = useCanonicalUrl(author.urlAlias);

  const titleTemplate = metaTags.controlled.title ? "%s" : DefaultTitleTemplate;
  const title = metaTags.controlled.title ?? author.name;

  const description = metaTags.controlled.description ?? author.bio ?? undefined;

  return (
    <PageSeo
      {...extraSeoOptions}
      additionalLinkTags={[rssFeedLink]}
      additionalMetaTags={[...keywordTags]}
      canonical={canonicalUrl}
      description={description}
      openGraph={{ description, url: canonicalUrl }}
      title={title}
      titleTemplate={titleTemplate}
    />
  );
};

AuthorSeo.displayName = "AuthorSeo";
